import React from "react";
import { Listbox, ListboxOption, ListboxOptions } from "@headlessui/react";
import { cva, VariantProps } from "class-variance-authority";
import clsx from "clsx";
import { HiCheck } from "react-icons/hi2";

import CustomListboxButton from "@/components/molecules/dropdown/CustomListboxButton";
import SelectedOptions from "@/components/molecules/SelectedOptions";

// TODO Maybe split this into 2 components like ComboBoxSingle and ComboBoxMultiple for more type safety and better readability

export const dropdownSizeVariants = cva(
  "relative flex bg-white w-full items-center gap-x-2 rounded-md border shadow-xs transition duration-75 hover:shadow-sm active:shadow-xs disabled:pointer-events-none disabled:bg-disabled",
  {
    variants: {
      sizing: {
        xs: "px-3.5 py-2 text-sm",
        sm: "px-3.5 py-2.5 text-sm",
        md: "px-3.5 py-2.5 text-base",
        lg: "px-3.5 py-3 text-base",
        xl: "p-3.5 text-xl",
      },
    },
    defaultVariants: {
      sizing: "sm",
    },
  },
);

type DropdownProps<T> = VariantProps<typeof dropdownSizeVariants> & {
  id?: string;
  options: T[];
  selected: T[] | T | null;
  onSelect: (selected: never) => void;
  displayValue?: (option: T) => React.ReactNode;
  placeholder?: string | React.ReactNode;
  multiple?: boolean;
  disabled?: boolean;
  renderOption?: (option: T) => React.ReactNode;
  renderPlaceholder?: React.ReactNode;
  renderChip?: (item: T, handleRemove: (item: T) => void) => React.ReactNode;
  className?: string;
  hasError?: boolean;
  leftPlaceholder?: React.ReactNode | string;
  customButton?: React.ReactNode;
};

const Dropdown = <T extends { id: string; name: string; disabled?: boolean }>({
  id,
  selected,
  onSelect,
  displayValue = (option) => option?.name,
  options,
  disabled,
  placeholder,
  multiple = false,
  renderOption,
  renderChip,
  renderPlaceholder,
  hasError,
  leftPlaceholder,
  className,
  customButton,
  sizing,
}: DropdownProps<T>) => {
  const handleRemoveSelectedItem = (item: T) => {
    if (multiple && Array.isArray(selected)) {
      const updatedValue = selected.filter((v) => v.id !== item.id);
      onSelect(updatedValue as never);
    }
  };

  return (
    <div className={"relative"}>
      <Listbox value={selected} onChange={onSelect} name={id} disabled={disabled} multiple={multiple}>
        {customButton ?? (
          <CustomListboxButton
            selected={selected}
            leftPlaceholder={leftPlaceholder}
            hasError={hasError}
            sizing={sizing}
            multiple={multiple}
            displayValue={displayValue}
            placeholder={placeholder}
            className={className}
          />
        )}

        <ListboxOptions
          anchor="bottom start"
          transition
          className={clsx(
            "mt-1 max-h-60 w-[var(--button-width)] min-w-52 overflow-auto rounded bg-white py-1 text-sm shadow-md focus:outline-none",
            "transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0",
          )}
        >
          {renderPlaceholder && (
            <ListboxOption
              as={"button"}
              value={null}
              className="group my-0.5 flex h-10 w-full select-none items-center justify-between gap-2 rounded px-3 py-2 font-medium text-primary data-[focus]:bg-secondary-hover"
            >
              <span className={"block truncate"}>{renderPlaceholder}</span>
            </ListboxOption>
          )}

          {options.map((option) => (
            <ListboxOption
              as={"button"}
              key={option.name}
              value={option}
              disabled={option.disabled}
              className="group my-0.5 flex w-full select-none items-center justify-between gap-2 rounded px-3 py-2 font-medium text-primary data-[focus]:bg-secondary-hover data-[selected]:bg-secondary data-[disabled]:opacity-40"
            >
              {renderOption ? renderOption(option) : <span className={"block truncate"}>{displayValue(option)}</span>}
              <HiCheck className="invisible size-6 fill-fg-brand-primary group-data-[selected]:visible" />
            </ListboxOption>
          ))}
        </ListboxOptions>
      </Listbox>

      {multiple && (
        <SelectedOptions
          value={selected as T[]}
          displayValue={displayValue}
          renderChip={renderChip}
          handleRemoveSelectedItem={handleRemoveSelectedItem}
        />
      )}
    </div>
  );
};

export default Dropdown;
